import React from 'react';
import { Helmet } from 'react-helmet';
import Nav from '../components/Nav';
import Footer from '../components/Footer';

const PrivacyPage: React.FC = () => {
  return (
    <div className="font-display text-ink bg-white min-h-screen">
      <Helmet>
        <title>Privacy Policy | DINK Social</title>
        <meta name="description" content="Read the DINK Social privacy policy. Learn how we collect, use, and protect your personal information on our childfree couples platform." />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Privacy Policy | DINK Social" />
        <meta property="og:description" content="Read the DINK Social privacy policy. Learn how we protect your personal information." />
        <meta property="og:url" content="https://dinksocial.com/privacy" />
        <meta property="og:site_name" content="DINK Social" />
        <meta property="og:image" content="https://dinksocial.com/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Privacy Policy | DINK Social" />
        <meta name="twitter:description" content="Read the DINK Social privacy policy." />
        <meta name="twitter:image" content="https://dinksocial.com/og-image.jpg" />
        <link rel="canonical" href="https://dinksocial.com/privacy" />
      </Helmet>

      <Nav />

      {/* ─── HEADING ───────────────────────────────────────── */}
      <section className="pt-32 md:pt-40 pb-12 md:pb-16 px-6 md:px-10">
        <div className="max-w-3xl mx-auto text-center">
          <h1
            className="font-display font-medium text-ink text-[42px] md:text-[56px] lg:text-[64px] mb-5"
            style={{ lineHeight: 1.02, letterSpacing: '-0.02em' }}
          >
            Privacy policy
          </h1>
          <p
            className="font-playfair italic font-medium text-ink/85 text-[20px] md:text-[24px]"
            style={{ lineHeight: 1.3, letterSpacing: '-0.015em' }}
          >
            How we collect, use, and protect your information
          </p>
        </div>
      </section>

      {/* ─── BODY ──────────────────────────────────────────── */}
      <section className="pb-20 md:pb-24 px-6 md:px-10">
        <div className="max-w-3xl mx-auto">
          <div className="prose prose-lg max-w-none
            prose-headings:font-display prose-headings:font-medium prose-headings:text-ink prose-headings:tracking-tight
            prose-h2:text-[26px] md:prose-h2:text-[32px] prose-h2:mt-12 prose-h2:mb-5 prose-h2:leading-tight
            prose-h3:text-[20px] md:prose-h3:text-[24px] prose-h3:mt-8 prose-h3:mb-3 prose-h3:leading-tight
            prose-p:text-ink/75 prose-p:text-[16px] md:prose-p:text-[17px] prose-p:leading-relaxed
            prose-a:text-primary prose-a:no-underline prose-a:font-medium hover:prose-a:text-primary-dark hover:prose-a:underline
            prose-strong:text-ink prose-strong:font-semibold
            prose-ul:text-ink/75 prose-li:text-[16px] md:prose-li:text-[17px] prose-li:leading-relaxed
            prose-li:marker:text-primary/60">
              <p className="text-ink/75 mb-2">
                <strong>Last Updated:</strong> August 23rd, 2026
              </p>
              <p className="text-ink/75 mb-2">
                <strong>Effective Date:</strong> June 28th, 2026
              </p>
              <p className="text-ink/75 mb-6">
                <strong>Version:</strong> 1.3
              </p>

              <p className="text-ink/75 mb-8">
                This Privacy Policy describes how DINK Social LLC ("we," "us," or "our") collects, uses, shares, and protects your personal information when you use our platform at dinksocial.com and our mobile applications.
              </p>

              {/* Section 1 */}
              <h2>1. Information We Collect</h2>

              <h3>1.1 Information You Provide</h3>
              <ul className="list-disc pl-6 text-ink/75 mb-4 space-y-2">
                <li><strong>Profile Information:</strong> Names, ages, relationship status, photos, interests, and lifestyle preferences</li>
                <li><strong>Contact Information:</strong> Email address for account management and communication</li>
                <li><strong>Forum Activity:</strong> Posts, comments, and interactions in community forums</li>
                <li><strong>Event Data:</strong> RSVPs, event creation, and attendance information</li>
                <li><strong>Verification Data:</strong> Information to verify your identity and relationship status</li>
                <li><strong>Communication Content:</strong> Messages, photos, and other content shared through our platform</li>
                <li><strong>Account Deletion Feedback:</strong> If you choose to tell us why you are deleting your account, we collect that reason and any optional comments. Answering is always optional and never required to delete your account.</li>
                <li>
                  <strong>Age Verification Data:</strong>
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>Date of birth to verify you meet our 18+ age requirement</li>
                    <li>This information is required for platform access and stored securely</li>
                    <li>We use date of birth solely for age verification purposes</li>
                    <li>We do not share your exact date of birth with other users or third parties</li>
                    <li>Retained for 7 years for legal compliance and age verification records</li>
                  </ul>
                </li>
              </ul>

              <h3>1.2 Automatically Collected Information</h3>
              <ul className="list-disc pl-6 text-ink/75 mb-6 space-y-2">
                <li><strong>Location Data:</strong> City-level location (since we are storing city, state, country - not precise GPS coordinates)</li>
                <li><strong>Device Information:</strong> IP address, browser type, operating system, and device identifiers</li>
                <li><strong>Usage Data:</strong> How you interact with our service, features used, and time spent. In the mobile app this is collected through PostHog, our product analytics provider, and is linked to your account, such as which features you use, events you RSVP to, invitations you send and accept, and when you send or receive messages. We never send the content of your messages or photos to our analytics tools.</li>
                <li><strong>Technical Data:</strong> Log files, cookies, and similar tracking technologies</li>
              </ul>

              {/* Section 2 */}
              <h2>2. How We Use Your Information</h2>

              <h3>2.1 Service Provision</h3>
              <ul className="list-disc pl-6 text-ink/75 mb-4 space-y-1">
                <li>Create and manage your couple profile</li>
                <li>Host and manage couple meetup events</li>
                <li>Facilitate forum discussions and community engagement</li>
                <li>Facilitate community discussions through city-based and global forums</li>
                <li>Enable couple-to-couple messaging and forum interactions</li>
                <li>Provide customer support and respond to inquiries</li>
              </ul>

              <h3>2.2 Service Improvement</h3>
              <ul className="list-disc pl-6 text-ink/75 mb-4 space-y-1">
                <li>Analyze community engagement to improve forum features and city expansion</li>
                <li>Develop new features and enhance user experience</li>
                <li>Conduct research and analytics for service optimization</li>
              </ul>

              <h3>2.3 Safety and Security</h3>
              <ul className="list-disc pl-6 text-ink/75 mb-6 space-y-1">
                <li>Detect and prevent fraud, abuse, and violations of our terms</li>
                <li>Protect the safety and security of our users</li>
                <li>Comply with legal obligations and law enforcement requests</li>
                <li>Monitor forum content for community guideline violations</li>
                <li>Couple verification to maintain authentic community</li>
              </ul>

              {/* Section 3 */}
              <h2>3. Information Sharing and Disclosure</h2>

              <h3>3.1 With Other Users</h3>
              <p className="text-ink/75 mb-4">
                Your profile information (photos, interests, lifestyle preferences, and city-level location) is shared with other couples. Your forum posts and replies are visible to all community members in your local lounge and/or global lounge.
              </p>

              <h3>3.2 Service Providers</h3>
              <p className="text-ink/75 mb-3">We share information with trusted third-party service providers who help us operate our platform, including:</p>
              <ul className="list-disc pl-6 text-ink/75 mb-4 space-y-1">
                <li>Supabase (cloud database, authentication, and file storage)</li>
                <li>Render (application hosting and infrastructure)</li>
                <li>Resend (email delivery service)</li>
                <li>PostHog (product analytics on our website and mobile app)</li>
                <li>Meta Platforms, Inc. (advertising attribution on dinksocial.com via the Meta Pixel, see Section 6.2)</li>
                <li>Google Fonts and Fontshare (web font delivery on dinksocial.com; your IP address is sent to these providers when fonts load in your browser)</li>
                <li>Customer support and communication tools</li>
                <li>Payment processing services (if applicable)</li>
              </ul>

              <h3>3.3 Legal Requirements</h3>
              <p className="text-ink/75 mb-4">
                We may disclose your information when required by law, court order, or government regulation, or to protect our rights, property, or safety.
              </p>

              <h3>3.4 Business Transfers</h3>
              <p className="text-ink/75 mb-6">
                In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of the transaction.
              </p>

              {/* Section 4 */}
              <h2>4. Data Security</h2>
              <p className="text-ink/75 mb-3">We implement industry-standard security measures including:</p>
              <ul className="list-disc pl-6 text-ink/75 mb-4 space-y-1">
                <li>Encryption of data in transit and at rest</li>
                <li>Regular security audits and vulnerability assessments</li>
                <li>Access controls and authentication systems</li>
                <li>Secure data centers with physical and digital safeguards</li>
                <li>Employee training on data protection and privacy</li>
              </ul>
              <p className="text-ink/75 mb-6">
                While we strive to protect your information, no method of transmission over the internet is 100% secure.
              </p>

              {/* Section 5 */}
              <h2>5. Your Privacy Rights</h2>

              <h3>5.1 GDPR Rights (EU Residents)</h3>
              <ul className="list-disc pl-6 text-ink/75 mb-4 space-y-1">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Rectification:</strong> Correct inaccurate or incomplete information</li>
                <li><strong>Erasure:</strong> Request deletion of your personal data</li>
                <li><strong>Portability:</strong> Export your data in a machine-readable format</li>
                <li><strong>Restriction:</strong> Limit how we process your data</li>
                <li><strong>Objection:</strong> Object to certain types of processing</li>
                <li><strong>Withdraw Consent:</strong> Revoke consent for data processing</li>
              </ul>

              <h3>5.2 CCPA / CPRA Rights (California Residents)</h3>
              <ul className="list-disc pl-6 text-ink/75 mb-4 space-y-1">
                <li>Know what personal information we collect and how it's used</li>
                <li>Request deletion of personal information</li>
                <li><strong>Opt-out of the sale or sharing of personal information.</strong> We do not sell your personal information for money. However, the Meta Pixel on dinksocial.com sends limited browsing data to Meta for advertising, which California law treats as "sharing" for cross-context behavioral advertising. You have the right to opt out of this sharing, and we describe how in Section 6.2 below</li>
                <li>Non-discrimination for exercising privacy rights</li>
              </ul>
              <p className="text-ink/75 mb-4">
                <strong>We honor the Global Privacy Control (GPC).</strong> If your browser or a browser extension sends a GPC signal, we automatically treat it as a request to opt out of sharing and do not load the Meta Pixel during your visit. You do not need to take any further action for that signal to apply. You can also opt out manually at any time using the <strong>"Do Not Sell or Share My Personal Information"</strong> link in the footer of every page on dinksocial.com.
              </p>

              <h3>5.3 How to Exercise Your Rights</h3>

              <p className="text-ink/75 mb-2"><strong>Data Export:</strong></p>
              <ul className="list-disc pl-6 text-ink/75 mb-4 space-y-1">
                <li>Email <a href="mailto:privacy@dinksocial.com" className="text-primary hover:underline">privacy@dinksocial.com</a> with your request</li>
              </ul>

              <p className="text-ink/75 mb-2"><strong>Account Deletion:</strong></p>
              <ul className="list-disc pl-6 text-ink/75 mb-4 space-y-1">
                <li>Log in to your DINK Social account</li>
                <li>Navigate to Settings → Account → Danger Zone</li>
                <li>Click "Permanently Delete Account"</li>
                <li>Follow the confirmation steps (you will be asked to type "DELETE" to confirm)</li>
                <li>Your account and data will be permanently deleted within 24 hours</li>
                <li>Alternatively, email <a href="mailto:privacy@dinksocial.com" className="text-primary hover:underline">privacy@dinksocial.com</a> with your request</li>
                <li>We will confirm your identity before processing any deletion request sent by email</li>
              </ul>

              <p className="text-ink/75 mb-2"><strong>Other Rights (Rectification, Restriction, Objection):</strong></p>
              <ul className="list-disc pl-6 text-ink/75 mb-4 space-y-1">
                <li>Email <a href="mailto:privacy@dinksocial.com" className="text-primary hover:underline">privacy@dinksocial.com</a> with your specific request</li>
                <li>Include your account email address for verification</li>
                <li>We will respond within 30 days (GDPR) or 45 days (CCPA)</li>
              </ul>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <p className="text-amber-800 font-medium text-sm mb-2"><strong>Important Notes:</strong></p>
                <ul className="list-disc pl-6 text-amber-800 text-sm space-y-1">
                  <li>Deleting your account is permanent and cannot be undone</li>
                  <li>Your forum posts will remain visible but will be anonymized (shown as "[deleted]")</li>
                  <li>Your partner will lose access to your shared couple profile</li>
                </ul>
              </div>

              {/* Section 6 */}
              <h2>6. Cookies and Tracking Technologies</h2>
              <p className="text-ink/75 mb-4">We use cookies to provide essential functionality for our service.</p>

              <h3>6.1 Essential Cookies (No Consent Required)</h3>
              <p className="text-ink/75 mb-3">We use the following cookies that are strictly necessary for our platform to function:</p>
              <p className="text-ink/75 mb-2"><strong>Authentication Cookies:</strong></p>
              <ul className="list-disc pl-6 text-ink/75 mb-4 space-y-1">
                <li><code className="bg-gray-100 px-1 rounded">sb-access-token</code> - Maintains your login session (expires: 1 hour)</li>
                <li><code className="bg-gray-100 px-1 rounded">sb-refresh-token</code> - Allows automatic session renewal (expires: 7 days)</li>
                <li>Session identifiers for security and fraud prevention</li>
              </ul>
              <p className="text-ink/75 mb-4">
                These cookies are essential for the service to work and cannot be disabled. Under GDPR Article 6(1)(b), consent is not required for cookies that are "strictly necessary" to provide the service you requested.
              </p>

              <h3>6.2 Analytics and Advertising Cookies (Website)</h3>
              <p className="text-ink/75 mb-3">On dinksocial.com, we use the following cookies and similar technologies to understand how people discover our app and to measure the effectiveness of our advertising:</p>

              <p className="text-ink/75 mb-2"><strong>Meta Pixel (Facebook / Instagram advertising):</strong></p>
              <ul className="list-disc pl-6 text-ink/75 mb-4 space-y-1">
                <li>Set by Meta Platforms, Inc. when you visit dinksocial.com</li>
                <li>Records pages you visit and clicks on our App Store / Google Play download buttons</li>
                <li>Allows us to measure which marketing channels and ads drive interest in our app, and to show relevant ads on Facebook and Instagram</li>
                <li>Meta may combine this information with data it already holds about you</li>
                <li><strong>How to opt out:</strong> use the <strong>"Do Not Sell or Share My Personal Information"</strong> link in our website footer, or enable Global Privacy Control (GPC) in your browser or a privacy extension. We automatically honor GPC and will not load the Meta Pixel for your visit. You can also opt out of Meta's interest-based advertising directly at <a href="https://www.facebook.com/help/568137493302217" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">facebook.com/help/568137493302217</a>, or block it with your browser's tracking-prevention settings or an ad-blocking extension</li>
              </ul>

              <p className="text-ink/75 mb-2"><strong>PostHog (product analytics):</strong></p>
              <ul className="list-disc pl-6 text-ink/75 mb-4 space-y-1">
                <li>Collects usage data on dinksocial.com (pages visited, buttons clicked, basic device information). For analytics in the mobile app, see Section 1.2.</li>
                <li>Helps us understand which features are useful and where the experience can be improved</li>
                <li>PostHog data is not shared with advertisers or used to target ads</li>
                <li>You can opt out via your browser's tracking-prevention settings or an ad-blocking extension</li>
              </ul>

              <p className="text-ink/75 mb-4">
                The Meta Pixel is <strong>only active on the dinksocial.com website</strong>. The DINK Social mobile app uses PostHog for product analytics; it does not use the Meta Pixel or any other third-party advertising SDK.
              </p>

              <h3>6.3 Managing Cookies</h3>
              <p className="text-ink/75 mb-6">
                You can delete cookies through your browser settings, but this will log you out and may prevent the platform from functioning properly.
              </p>

              <h3>6.4 Website Visitors Without an Account</h3>
              <p className="text-ink/75 mb-6">
                Most of this policy describes information tied to a DINK Social account. The analytics and advertising technologies in Section 6.2 also apply to people who simply browse dinksocial.com without signing up. If you do not have an account, you can still control this tracking at any time using the methods in Sections 5.2 and 6.2, including the Global Privacy Control signal (which we honor automatically) and your browser's privacy settings. Essential cookies (Section 6.1) only apply once you log in.
              </p>

              {/* Section 7 */}
              <h2>7. Data Retention</h2>

              <h3>7.1 Active Accounts</h3>
              <p className="text-ink/75 mb-4">
                We retain your information for as long as your account is active or as needed to provide you services.
              </p>

              <h3>7.2 Account Deletion</h3>
              <p className="text-ink/75 mb-3">When you permanently delete your account through Settings → Account → Delete Account:</p>

              <p className="text-ink/75 mb-2"><strong>Immediately Deleted (Within 24 Hours):</strong></p>
              <ul className="list-disc pl-6 text-ink/75 mb-4 space-y-1">
                <li>Your user profile (name, email, age, profile photo)</li>
                <li>Your couple profile (shared profile with your partner)</li>
                <li>All photos you uploaded (profile and couple gallery)</li>
                <li>Your reactions and likes</li>
                <li>Partner invitations</li>
                <li>Your notification history</li>
                <li>All personally identifiable information (PII)</li>
              </ul>

              <p className="text-ink/75 mb-2"><strong>Preserved (Anonymized):</strong></p>
              <ul className="list-disc pl-6 text-ink/75 mb-4 space-y-1">
                <li>Forum posts you created remain visible to preserve community discussions</li>
                <li>Posts are shown as created by "[deleted]" instead of your couple name</li>
                <li>Post content remains unchanged (valuable to the community)</li>
                <li>This is similar to how Reddit and other forums handle deletions</li>
              </ul>

              <p className="text-ink/75 mb-2"><strong>Audit Records:</strong></p>
              <ul className="list-disc pl-6 text-ink/75 mb-4 space-y-1">
                <li>Deletion requests are logged for compliance purposes (7 years)</li>
                <li>Audit logs contain only: user ID, action type, timestamp, IP address</li>
                <li>No personal content is retained in audit logs</li>
              </ul>

              <p className="text-ink/75 mb-2"><strong>Deletion Feedback (Optional):</strong></p>
              <ul className="list-disc pl-6 text-ink/75 mb-4 space-y-1">
                <li>When you delete your account, we may ask why. Answering is optional.</li>
                <li>We store your reason and any comments separately from your account, so they are not linked back to you.</li>
                <li>Free-text comments are deleted within 12 months.</li>
                <li>The general reason, with no information identifying you, may be kept longer for analytics to help us improve the service.</li>
                <li>For users in the EU/UK, we process this feedback under our legitimate interest (GDPR Article 6(1)(f)) in understanding why members leave and improving the service.</li>
              </ul>

              <h3>7.3 Important: Couple Profile Deletion</h3>
              <p className="text-ink/75 mb-3">DINK Social is a couples platform. When you delete your account:</p>
              <ul className="list-disc pl-6 text-ink/75 mb-4 space-y-1">
                <li>Your couple's shared profile is also deleted</li>
                <li>Your partner loses access to the shared couple profile</li>
                <li>Your partner's individual account remains active</li>
                <li>Your partner can create a new couple profile with someone else</li>
              </ul>
              <p className="text-ink/75 mb-4">
                This ensures clean data separation and prevents "orphaned" couple profiles.
              </p>

              <h3>7.4 Partner Leaves Platform</h3>
              <p className="text-ink/75 mb-3">If your partner deletes their account but you want to stay:</p>
              <ul className="list-disc pl-6 text-ink/75 mb-4 space-y-1">
                <li>You retain access to your individual account</li>
                <li>Your shared couple profile will be deleted</li>
                <li>You can create a new couple profile with a new partner</li>
                <li>Your forum activity (posts/replies) remains visible but anonymized</li>
              </ul>

              <h3>7.5 Retention Periods by Data Type</h3>
              <div className="overflow-x-auto mb-4">
                <table className="min-w-full border border-ink/10 rounded-lg">
                  <thead className="bg-ink/5">
                    <tr>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-ink/80 border-b">Data Type</th>
                      <th className="px-4 py-2 text-left text-sm font-semibold text-ink/80 border-b">Retention Period</th>
                    </tr>
                  </thead>
                  <tbody className="text-ink/75 text-sm">
                    <tr className="border-b">
                      <td className="px-4 py-2">Active account data</td>
                      <td className="px-4 py-2">While account is active</td>
                    </tr>
                    <tr className="border-b">
                      <td className="px-4 py-2">Deleted account PII</td>
                      <td className="px-4 py-2">Deleted within 24 hours</td>
                    </tr>
                    <tr className="border-b">
                      <td className="px-4 py-2">Anonymized forum posts</td>
                      <td className="px-4 py-2">Indefinite (community value)</td>
                    </tr>
                    <tr className="border-b">
                      <td className="px-4 py-2">Audit/compliance logs</td>
                      <td className="px-4 py-2">7 years</td>
                    </tr>
                    <tr className="border-b">
                      <td className="px-4 py-2">Account deletion comments (free text)</td>
                      <td className="px-4 py-2">Within 12 months</td>
                    </tr>
                    <tr className="border-b">
                      <td className="px-4 py-2">Account deletion reason (anonymized)</td>
                      <td className="px-4 py-2">Retained for analytics</td>
                    </tr>
                    <tr className="border-b">
                      <td className="px-4 py-2">Email communications</td>
                      <td className="px-4 py-2">3 years</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2">Payment records (if applicable)</td>
                      <td className="px-4 py-2">7 years (legal requirement)</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Section 8 */}
              <h2>8. International Data Transfers</h2>
              <p className="text-ink/75 mb-6">
                Your information may be transferred to and processed in countries other than your own. We currently limit data requirements to name, age, occupation, location, and email address.
              </p>

              {/* Section 9 */}
              <h2>9. Children's Privacy</h2>
              <p className="text-ink/75 mb-6">
                Our service is not intended for individuals under 18 years of age. We do not knowingly collect personal information from children under 18.
              </p>

              {/* Section 10 */}
              <h2>10. Changes to This Policy</h2>

              <h3>10.1 Policy Updates</h3>
              <p className="text-ink/75 mb-3">We may update this Privacy Policy periodically to reflect:</p>
              <ul className="list-disc pl-6 text-ink/75 mb-4 space-y-1">
                <li>Changes in our data practices</li>
                <li>New features or services</li>
                <li>Legal or regulatory requirements</li>
                <li>User feedback and best practices</li>
              </ul>

              <h3>10.2 Notification of Changes</h3>
              <p className="text-ink/75 mb-3">For significant changes, we will notify you by:</p>
              <ul className="list-disc pl-6 text-ink/75 mb-4 space-y-1">
                <li>Email to your registered email address (at least 30 days before changes take effect)</li>
                <li>Prominent notice on our website and within the application</li>
                <li>In-app notification upon your next login</li>
              </ul>

              <h3>10.3 Minor Updates</h3>
              <p className="text-ink/75 mb-3">For minor changes (typos, clarifications, formatting), we will:</p>
              <ul className="list-disc pl-6 text-ink/75 mb-4 space-y-1">
                <li>Update the "Last Updated" date at the top of this policy</li>
                <li>Post the updated policy on our website</li>
                <li>Not require re-acceptance unless material changes are made</li>
              </ul>

              <h3>10.4 Your Choices</h3>
              <p className="text-ink/75 mb-3">When we make significant changes:</p>
              <ul className="list-disc pl-6 text-ink/75 mb-4 space-y-1">
                <li>You will be asked to review and accept the updated Privacy Policy</li>
                <li>You can choose to decline and delete your account if you disagree</li>
                <li>Continued use of DINK Social after the effective date constitutes acceptance</li>
              </ul>

              <h3>10.5 Version History</h3>
              <p className="text-ink/75 mb-2"><strong>Current Version:</strong> 1.4</p>
              <p className="text-ink/75 mb-2"><strong>Last Updated:</strong> August 23rd, 2026</p>
              <p className="text-ink/75 mb-2"><strong>Version 1.4 (August 23rd, 2026):</strong> Disclosed that in-app usage analytics are collected through PostHog and linked to your account (features used, event RSVPs, invitations sent and accepted, and when messages are sent or received), and that message and photo content is never sent to analytics tools. Clarified that Section 6.2's cookie disclosures cover the website only. No change to the data actually collected by the mobile app; this update documents existing practice.</p>
              <p className="text-ink/75 mb-2"><strong>Version 1.3 (June 28th, 2026):</strong> Disclosed optional account-deletion feedback (an optional reason and free-text comments collected at deletion), its separate storage and retention (comments deleted within 12 months; the anonymized reason kept longer for analytics), and the legitimate-interest basis for processing it. Also removed an outdated section describing account deactivation, which the product does not offer; account deletion is unaffected. No change to data collected by the mobile app beyond this optional feedback.</p>
              <p className="text-ink/75 mb-2"><strong>Version 1.2 (June 24th, 2026):</strong> Added the California (CPRA) right to opt out of "sharing" for the Meta Pixel, adopted automatic honoring of the Global Privacy Control signal, clarified how website visitors without an account can opt out of analytics and advertising, and disclosed web font providers. No change to data collected by the mobile app.</p>
              <p className="text-ink/75 mb-2"><strong>Version 1.1 (May 1st, 2026):</strong> Disclosed use of the Meta Pixel and PostHog on dinksocial.com for advertising attribution and product analytics. No change to data collected by the mobile app.</p>
              <p className="text-ink/75 mb-2"><strong>Version 1.0 (October 25th, 2025):</strong> Initial publication.</p>
              <p className="text-ink/75 mb-6">
                We maintain a history of policy versions available upon request by emailing <a href="mailto:privacy@dinksocial.com" className="text-primary hover:underline">privacy@dinksocial.com</a>.
              </p>

              {/* Section 11 */}
              <h2>11. Consent and Agreement</h2>

              <h3>11.1 How You Provide Consent</h3>
              <p className="text-ink/75 mb-3">By creating an account on DINK Social, you:</p>
              <ul className="list-disc pl-6 text-ink/75 mb-4 space-y-1">
                <li>Confirm you are at least 18 years old</li>
                <li>Confirm you have read and understood this Privacy Policy</li>
                <li>Agree to the collection, use, and sharing of your information as described in this Privacy Policy</li>
                <li>Consent to the use of cookies and tracking technologies as described in Section 6</li>
              </ul>
              <p className="text-ink/75 mb-4">
                If you browse dinksocial.com without creating an account, you have not provided this consent. For that reason, the non-essential website tracking described in Section 6.2 can be opted out of at any time using the controls in Sections 5.2 and 6.2, and we honor the Global Privacy Control signal automatically.
              </p>

              <h3>11.2 Registration Consent</h3>
              <p className="text-ink/75 mb-3">During registration, you will be asked to check a box confirming:</p>
              <div className="bg-ink/5 border border-ink/10 rounded-lg p-4 mb-4">
                <p className="text-ink/80 italic">"I have read and agree to the Privacy Policy and Terms of Service"</p>
              </div>
              <p className="text-ink/75 mb-3">This consent is required to create an account. We record:</p>
              <ul className="list-disc pl-6 text-ink/75 mb-4 space-y-1">
                <li>Date and time of consent</li>
                <li>Version of Privacy Policy you agreed to</li>
                <li>Your IP address (for verification purposes)</li>
              </ul>

              <h3>11.3 Withdrawing Consent</h3>
              <p className="text-ink/75 mb-3">You can withdraw your consent at any time by:</p>

              <p className="text-ink/75 mb-2"><strong>Full Withdrawal (Account Deletion):</strong></p>
              <ul className="list-disc pl-6 text-ink/75 mb-4 space-y-1">
                <li>Navigate to Settings → Danger Zone → Delete Account</li>
                <li>This permanently deletes your account and all personal data</li>
                <li>Forum posts remain anonymized for community value</li>
              </ul>

              <p className="text-ink/75 mb-2"><strong>Partial Withdrawal (Specific Purposes):</strong></p>
              <ul className="list-disc pl-6 text-ink/75 mb-4 space-y-1">
                <li>Email <a href="mailto:privacy@dinksocial.com" className="text-primary hover:underline">privacy@dinksocial.com</a> to withdraw consent for specific purposes</li>
                <li>Example: Opt-out of marketing emails while keeping account active</li>
                <li>We will honor your request within 30 days</li>
              </ul>

              <p className="text-ink/75 mb-2"><strong>Marketing Consent:</strong></p>
              <ul className="list-disc pl-6 text-ink/75 mb-4 space-y-1">
                <li>You can opt-out of marketing emails at any time</li>
                <li>Click "Unsubscribe" in any marketing email</li>
                <li>Or update preferences in Settings → Notifications</li>
              </ul>

              <h3>11.4 Consent Records</h3>
              <p className="text-ink/75 mb-3">We maintain records of your consent including:</p>
              <ul className="list-disc pl-6 text-ink/75 mb-4 space-y-1">
                <li>Date of consent</li>
                <li>Privacy Policy version accepted</li>
                <li>IP address at time of consent</li>
                <li>Consent preferences (marketing, cookies, etc.)</li>
              </ul>
              <p className="text-ink/75 mb-4">
                These records are kept for compliance purposes and to honor your privacy choices.
              </p>

              <h3>11.5 Changes to Consent</h3>
              <p className="text-ink/75 mb-3">If we make significant changes to how we use your data:</p>
              <ul className="list-disc pl-6 text-ink/75 mb-6 space-y-1">
                <li>We will notify you by email</li>
                <li>You will be asked to review and accept the updated Privacy Policy</li>
                <li>Continued use of the service after notification constitutes acceptance</li>
                <li>You may delete your account if you do not agree to the changes</li>
              </ul>

              {/* Section 12 */}
              <h2>12. Contact Information</h2>
              <p className="text-ink/75 mb-4">
                For questions about this Privacy Policy or to exercise your privacy rights, please contact us at:
              </p>
              <div className="bg-ink/5 border border-ink/10 rounded-lg p-4 mb-6">
                <p className="text-ink/80 mb-2">
                  <strong>Email:</strong> <a href="mailto:privacy@dinksocial.com" className="text-primary hover:underline">privacy@dinksocial.com</a>
                </p>
                <p className="text-ink/80 mb-2">
                  <strong>Address:</strong> 1235 East Blvd, Ste E PMB 2310, Charlotte, NC 28203
                </p>
                <p className="text-ink/80">
                  <strong>Legal Entity:</strong> DINK Social LLC
                </p>
              </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPage;
